<template>
  <master-layout
    ref="masterLayout"
    :show-footer="true"
    :full-width="true"
    :enable-scroll-event="true"
    background-color="#191817"
  >
    <div class="bg-[#191817]">
      <HeaderSection :is-hidden="true" />
      <Loading v-if="isLoading" />
      <div
        v-else-if="viewedSermon && viewedSermon.playerSettings"
        class="pb-[40px] font-inter text-[#ECEBEB]"
      >
        <div class="lg:mt-[40px] w-full mx-auto max-w-[1032px]">
          <div class="w-full flex flex-col lg:flex-row">
            <div
              class="w-full lg:max-w-[1032px]"
              :class="{
                'mx-auto lg:mr-[40px]':
                  viewedSermon.videoChapters && viewedSermon.videoChapters.length > 0,
              }"
            >
              <div
                v-if="viewedSermon.playerSettings.playerType === 'nativeVideo'"
                class="w-full px-[20px] mt-[16px]"
              >
                <VideoPlayer
                  :key="slug"
                  :id="slug"
                  :ref="`sermon-component-${slug}`"
                  :metadata="{
                    url: viewedSermon.playerSettings.resourceUrl,
                    thumbnail: viewedSermon.thumbnail,
                    title: viewedSermon.title,
                  }"
                />
              </div>
              <div
                v-if="viewedSermon.playerSettings.playerType === 'nativeAudio'"
                class="w-full px-[20px] mt-[16px]"
              >
                <AudioPlayer
                  :key="slug"
                  ref="audioPlayer"
                  :audio-url="viewedSermon.playerSettings.resourceUrl"
                  :audio-image="viewedSermon.thumbnail"
                  :title="viewedSermon.title"
                />
              </div>
              <div
                v-if="viewedSermon.playerSettings.playerType === 'embedYoutube'"
                class="mt-[16px]"
              >
                <ProxyPlayer
                  :key="slug"
                  :id="slug"
                  :video-url="viewedSermon.playerSettings.resourceUrl"
                  :metadata="{
                    title: viewedSermon.title,
                  }"
                />
              </div>
              <div
                v-if="viewedSermon.playerSettings.playerType === 'embedSpotify'"
                class="px-[20px] mt-[16px]"
              >
                <SpotifyPlayer :key="slug" :audio-url="viewedSermon.playerSettings.resourceUrl" />
              </div>
            </div>
          </div>
          <div class="my-[48px] px-[20px]">
            <h1 class="text-[1.75rem] leading-[2rem] font-black tracking-[-0.84px] mb-[8px]">
              {{ viewedSermon.title }}
            </h1>
            <div class="text-[#ECEBEB]">
              <div class="flex flex-row items-center mb-[24px]">
                <span class="text-[1rem] leading-[1.5rem] font-medium">
                  {{ pastorNames }}
                </span>
                <template v-if="formattedSermonDate">
                  <span class="text-[1rem] leading-[1.5rem] font-medium text-[#C3C2C1] mx-[4px]"
                    >·</span
                  >
                  <span
                    class="text-[1rem] leading-[1.5rem] font-medium text-[#C3C2C1] whitespace-nowrap"
                  >
                    {{ formattedSermonDate }}
                  </span>
                </template>
              </div>
              <div>
                <p
                  v-if="!showFullDescription"
                  class="line-clamp-2 text-[1rem] leading-[1.5rem] font-normal"
                >
                  {{ viewedSermon.description }}
                </p>
                <p v-else class="text-[1rem] leading-[1.5rem] font-normal">
                  {{ viewedSermon.description }}
                </p>
                <button
                  v-if="viewedSermon.description && viewedSermon.description.length > 100"
                  @click="showFullDescription = !showFullDescription"
                  class="text-[#F1F1F1] underline mt-2"
                >
                  {{ showFullDescription ? 'Read less' : 'Read more' }}
                </button>
              </div>
              <div
                v-if="viewedSermon.videoChapters && viewedSermon.videoChapters.length > 0"
                class="w-full mx-auto lg:max-w-[312px] mt-[24px]"
              >
                <VideoChapters
                  :video-chapters="viewedSermon.videoChapters"
                  @jumpToChapter="jumpToChapter"
                />
              </div>
              <div class="grid gap-[16px] mt-[24px]">
                <div
                  v-if="viewedSermon.sermonLinks?.youtubeLink"
                  @click="openPage(viewedSermon.sermonLinks.youtubeLink)"
                >
                  <img
                    class="w-[120px] h-[40px]"
                    src="/assets/icons/library/youtube_share_button.png"
                  />
                </div>
                <div
                  v-if="viewedSermon.sermonLinks?.spotifyLink"
                  @click="openPage(viewedSermon.sermonLinks.spotifyLink)"
                >
                  <img
                    class="w-[120px] h-[40px]"
                    src="/assets/icons/library/spotify_share_button.png"
                  />
                </div>
              </div>
              <ShareButton
                v-if="viewedSermon.sharingLink"
                id="share-button"
                class="h-[28px]"
                :event-name="viewedSermon.title"
                :sharing-url="viewedSermon.sharingLink"
              />
              <!-- Downloadables Slider -->
              <DownloadablesSlider
                v-if="viewedSermon.downloadables?.length > 0"
                class="mt-[40px]"
                ref="library-sermon-page-downloadables"
                baseSwiperId="library-sermon-page-downloadables"
                :items="viewedSermon.downloadables"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapState } from 'vuex';
import MasterLayout from '@/components/MasterLayout.vue';
import HeaderSection from './components/HeaderSection.vue';
import VideoPlayer from '@/components/Library/Sermon/VideoPlayer';
import VideoChapters from '@/components/Library/Sermon/VideoChapters';
import ShareButton from '@/components/Library/ShareButton';
import ProxyPlayer from '@/components/Library/Sermon/ProxyPlayer.vue';
import SpotifyPlayer from '@/components/Library/Sermon/SpotifyPlayer.vue';
import AudioPlayer from '@/components/Library/Sermon/AudioPlayer.vue';
import DownloadablesSlider from '@/components/ServiceHighlightsPage/DownloadablesSlider.vue';
import { openUrl } from '@/utils/browser-helper';
import { formatSermonDate } from '@/utils/library-helpers';
import Loading from '@/components/common/LoadingState.vue';
import { trackSermonOpened } from '@/utils/analytics';

export default {
  name: 'Sermon',
  components: {
    MasterLayout,
    HeaderSection,
    VideoPlayer,
    VideoChapters,
    ShareButton,
    ProxyPlayer,
    SpotifyPlayer,
    AudioPlayer,
    DownloadablesSlider,
    Loading,
  },
  data() {
    return {
      showFullDescription: false,
      isLoading: false,
      lastTrackedSermonSlug: null,
    };
  },
  computed: {
    ...mapState('library', ['viewedSermon']),
    slug() {
      return this.$route.params.slug;
    },
    formattedSermonDate() {
      return formatSermonDate(this.viewedSermon?.date);
    },
    pastorNames() {
      return this.viewedSermon?.pastors?.map(p => p.name).join(', ') || '';
    },
  },
  watch: {
    slug(newSlug, oldSlug) {
      if (!newSlug || newSlug === oldSlug) return;
      this.scheduleSermonLoad();
    },
  },
  methods: {
    jumpToChapter(_title, timestamp) {
      const splitTimestamp = timestamp.split(':');
      const seconds = +splitTimestamp[0] * 60 + +splitTimestamp[1];
      this.$refs[`sermon-component-${this.slug}`]?.seek(seconds);
      this.$refs[`sermon-component-${this.slug}`]?.play();
    },
    async openPage(sharingLink) {
      await openUrl(
        { url: sharingLink },
        {
          title: 'Library Share',
          source: 'library-share',
          page: 'Library',
        },
      );
    },
    pausePlayers() {
      this.$refs[`sermon-component-${this.slug}`]?.pause();
      this.$refs.audioPlayer?.pause();
    },
    // One fetch per navigation when ionViewWillEnter and slug watch both fire.
    scheduleSermonLoad() {
      const slug = this.$route.params.slug;
      if (!slug) return Promise.resolve();

      if (this._sermonLoadCoalescePending && this._sermonLoadPromise) {
        return this._sermonLoadPromise;
      }

      this._sermonLoadCoalescePending = true;
      this._sermonLoadPromise = new Promise(resolve => {
        this.$nextTick(async () => {
          this._sermonLoadCoalescePending = false;
          try {
            await this.loadSermonForRoute();
          } finally {
            resolve();
            this._sermonLoadPromise = null;
          }
        });
      });
      return this._sermonLoadPromise;
    },
    async loadSermonForRoute() {
      const slug = this.$route.params.slug;
      if (!slug) return;
      try {
        this.isLoading = true;
        await this.$store.dispatch('library/fetchSermon', slug);
        this.trackSermonOpenedOnce(slug);
      } finally {
        this.isLoading = false;
      }
    },
    trackSermonOpenedOnce(slug) {
      if (!slug || this.lastTrackedSermonSlug === slug) return;

      trackSermonOpened({
        slug,
        title: this.viewedSermon?.title,
        source: 'sermon-detail',
      });
      this.lastTrackedSermonSlug = slug;
    },
  },
  async ionViewWillEnter() {
    await this.scheduleSermonLoad();
  },

  ionViewWillLeave() {
    this.pausePlayers();
  },

  ionViewDidLeave() {
    this.pausePlayers();
  },

  beforeUnmount() {
    this.pausePlayers();
  },
};
</script>
